export type PostalAddressSchemaInput = {
  streetAddress?: string | null;
  postalCode?: string | null;
  addressLocality?: string | null;
  addressRegion?: string | null;
  addressCountry?: string | null;
};

export type GeoSchemaInput = {
  latitude?: number | string | null;
  longitude?: number | string | null;
};

export type BreadcrumbSchemaItem = {
  name: string;
  url?: string;
};

export type VenueSchemaInput = {
  name: string;
  canonicalUrl: string;
  description?: string | null;
  address?: PostalAddressSchemaInput | null;
  geo?: GeoSchemaInput | null;
  imageUrls?: string[];
};

export type EventSchemaInput = {
  name: string;
  canonicalUrl: string;
  startDate: string;
  endDate?: string | null;
  timeZone?: string | null;
  description?: string | null;
  imageUrls?: string[];
  status?: string | null;
  isCanceled?: boolean;
  previousStartDate?: string | null;
  isPrivate?: boolean;
  venue: {
    name: string;
    canonicalUrl: string;
    address?: PostalAddressSchemaInput | null;
    geo?: GeoSchemaInput | null;
  };
  offer?: {
    price: number;
    currency: string;
    soldOut?: boolean;
  } | null;
  now?: number;
};

export type TicketOfferInput = {
  price?: number | null;
  currency?: string | null;
};

type JsonLdObject = Record<string, unknown>;
type DateParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const OFFSET_AWARE_DATE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,9})?)?(Z|([+-])(\d{2}):(\d{2}))$/i;
const NAIVE_DATE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,9})?)?$/;
const datePartsFormatters = new Map<string, Intl.DateTimeFormat>();

export function buildBreadcrumbSchema(
  items: BreadcrumbSchemaItem[],
): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name.trim(),
      ...(item.url ? { item: item.url } : {}),
    })),
  };
}

export function buildVenueSchema(input: VenueSchemaInput): JsonLdObject | null {
  const name = cleanText(input.name);
  if (!name || !isHttpUrl(input.canonicalUrl)) return null;

  const schema: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${input.canonicalUrl}#localbusiness`,
    name,
    "url": input.canonicalUrl,
  };
  const description = cleanDescription(input.description);
  const address = buildPostalAddress(input.address);
  const geo = buildGeo(input.geo);
  const images = validImageUrls(input.imageUrls);

  if (description) schema.description = description;
  if (address) schema.address = address;
  if (geo) schema.geo = geo;
  if (images.length > 0) schema.image = images;

  return schema;
}

export function buildEventSchema(input: EventSchemaInput): JsonLdObject | null {
  const name = cleanText(input.name);
  const startDate = toOffsetAwareDate(input.startDate, input.timeZone);
  const address = buildPostalAddress(input.venue.address);
  const venueName = cleanText(input.venue.name);

  if (
    input.isPrivate ||
    !name ||
    !venueName ||
    !address ||
    !startDate ||
    !isHttpUrl(input.canonicalUrl) ||
    !isHttpUrl(input.venue.canonicalUrl)
  ) {
    return null;
  }

  const place: JsonLdObject = {
    "@type": "Place",
    "@id": `${input.venue.canonicalUrl}#place`,
    "name": venueName,
    "url": input.venue.canonicalUrl,
    address,
  };
  const geo = buildGeo(input.venue.geo);
  if (geo) place.geo = geo;

  const schema: JsonLdObject = {
    "@context": "https://schema.org",
    "@type": "Event",
    "@id": `${input.canonicalUrl}#event`,
    name,
    startDate,
    "eventStatus": schemaEventStatus(input.status, input.isCanceled),
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "url": input.canonicalUrl,
    "location": place,
    "organizer": {
      "@type": "Organization",
      "@id": `${input.venue.canonicalUrl}#organizer`,
      "name": venueName,
      "url": input.venue.canonicalUrl,
    },
  };

  const endDate = input.endDate
    ? toOffsetAwareDate(input.endDate, input.timeZone)
    : undefined;
  const previousStartDate = input.previousStartDate
    ? toOffsetAwareDate(input.previousStartDate, input.timeZone)
    : undefined;
  const description = cleanDescription(input.description);
  const images = validImageUrls(input.imageUrls);
  const offer = shouldIncludeOffer(input, startDate, endDate)
    ? buildOffer(input.offer, input.canonicalUrl)
    : undefined;

  if (endDate) schema.endDate = endDate;
  if (
    schema.eventStatus === "https://schema.org/EventRescheduled" &&
    previousStartDate
  ) {
    schema.previousStartDate = previousStartDate;
  }
  if (description) schema.description = description;
  if (images.length > 0) schema.image = images;
  if (offer) schema.offers = offer;

  return schema;
}

export function toOffsetAwareDate(
  value: string | null | undefined,
  timeZone?: string | null,
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const offsetAwareMatch = OFFSET_AWARE_DATE.exec(trimmed);
  if (offsetAwareMatch) {
    const parts = datePartsFromMatch(offsetAwareMatch);
    if (!isValidDateParts(parts)) return undefined;

    const offset = offsetAwareMatch[8];
    if (!offset) return undefined;
    if (offset.toUpperCase() !== "Z") {
      const offsetHours = Number(offsetAwareMatch[10]);
      const offsetMinutes = Number(offsetAwareMatch[11]);
      if (
        offsetHours > 14 ||
        offsetMinutes > 59 ||
        (offsetHours === 14 && offsetMinutes !== 0)
      ) {
        return undefined;
      }
    }
    return Number.isNaN(Date.parse(trimmed)) ? undefined : trimmed;
  }

  const match = NAIVE_DATE.exec(trimmed);
  if (!match || !timeZone) return undefined;

  const parts = datePartsFromMatch(match);
  if (!isValidDateParts(parts)) return undefined;
  const { year, month, day, hour, minute, second } = parts;

  const localAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const possibleOffsets = new Set<number>();
  for (let hours = -48; hours <= 48; hours += 6) {
    const offset = timeZoneOffsetMinutes(
      new Date(localAsUtc + hours * 60 * 60 * 1000),
      timeZone,
    );
    if (offset !== undefined) possibleOffsets.add(offset);
  }

  const matchingInstants = [...possibleOffsets]
    .map((offsetMinutes) => ({
      instant: new Date(localAsUtc - offsetMinutes * 60_000),
      offsetMinutes,
    }))
    .filter(({ instant }) =>
      localPartsMatch(instant, timeZone, {
        year,
        month,
        day,
        hour,
        minute,
        second,
      }),
    );
  const matchingInstant =
    matchingInstants.length === 1 ? matchingInstants[0] : undefined;
  if (!matchingInstant) return undefined;

  const fraction = match[7] ?? "";
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}${fraction}${formatOffset(matchingInstant.offsetMinutes)}`;
}

export function getLowestTicketOffer(
  tickets: TicketOfferInput[],
): { price: number; currency: string } | null {
  let selected: TicketOfferInput | undefined;
  for (const ticket of tickets) {
    if (ticket.price === null || ticket.price === undefined) continue;
    const price = Number(ticket.price);
    if (!Number.isFinite(price) || price < 0) continue;
    if (!selected || price < Number(selected.price)) selected = ticket;
  }

  if (
    !selected ||
    typeof selected.currency !== "string" ||
    !selected.currency.trim()
  )
    return null;
  return { price: Number(selected.price), currency: selected.currency };
}

function buildPostalAddress(
  input?: PostalAddressSchemaInput | null,
): JsonLdObject | undefined {
  if (!input) return undefined;
  const fields = {
    streetAddress: cleanText(input.streetAddress),
    postalCode: cleanText(input.postalCode),
    addressLocality: cleanText(input.addressLocality),
    addressRegion: cleanText(input.addressRegion),
    addressCountry: cleanText(input.addressCountry),
  };
  const present = Object.entries(fields).filter(
    (entry): entry is [string, string] => Boolean(entry[1]),
  );
  if (present.length === 0) return undefined;
  return { "@type": "PostalAddress", ...Object.fromEntries(present) };
}

function buildGeo(input?: GeoSchemaInput | null): JsonLdObject | undefined {
  if (
    input?.latitude === null ||
    input?.latitude === undefined ||
    input?.latitude === "" ||
    input?.longitude === null ||
    input?.longitude === undefined ||
    input?.longitude === ""
  ) {
    return undefined;
  }
  const latitude = Number(input?.latitude);
  const longitude = Number(input?.longitude);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return undefined;
  }
  return { "@type": "GeoCoordinates", latitude, longitude };
}

function buildOffer(
  input: EventSchemaInput["offer"],
  canonicalUrl: string,
): JsonLdObject | undefined {
  if (!input || !Number.isFinite(input.price) || input.price < 0)
    return undefined;
  const currency = input.currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) return undefined;

  return {
    "@type": "Offer",
    "price": input.price,
    "priceCurrency": currency,
    "availability": input.soldOut
      ? "https://schema.org/SoldOut"
      : "https://schema.org/InStock",
    "url": canonicalUrl,
  };
}

function shouldIncludeOffer(
  input: EventSchemaInput,
  startDate: string,
  endDate: string | undefined,
): boolean {
  const status = normalizedEventStatus(input.status);
  if (
    input.isCanceled ||
    [
      "canceled",
      "cancelled",
      "closed",
      "completed",
      "ended",
      "finished",
      "past",
    ].includes(status)
  ) {
    return false;
  }

  const lastEventDate = endDate ?? startDate;
  const lastEventTime = Date.parse(lastEventDate);
  return (
    Number.isNaN(lastEventTime) || lastEventTime >= (input.now ?? Date.now())
  );
}

function schemaEventStatus(
  status?: string | null,
  isCanceled?: boolean,
): string {
  if (isCanceled) return "https://schema.org/EventCancelled";
  const normalized = normalizedEventStatus(status).replace(/-/g, "");
  if (normalized === "canceled" || normalized === "cancelled") {
    return "https://schema.org/EventCancelled";
  }
  if (normalized === "postponed") return "https://schema.org/EventPostponed";
  if (normalized === "rescheduled")
    return "https://schema.org/EventRescheduled";
  return "https://schema.org/EventScheduled";
}

function normalizedEventStatus(status?: string | null): string {
  return (status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/-+/g, "-");
}

function validImageUrls(urls: string[] | undefined): string[] {
  return [...new Set((urls ?? []).map((url) => url.trim()).filter(isHttpUrl))];
}

function cleanDescription(value?: string | null): string | undefined {
  const text = cleanText(value);
  if (!text) return undefined;
  const normalized = text.toLowerCase();
  if (
    normalized === "event description not available" ||
    normalized === "description not available" ||
    normalized === "-event-description" ||
    normalized === "-company-description"
  ) {
    return undefined;
  }
  return text;
}

function cleanText(value?: string | null): string | undefined {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized || undefined;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function datePartsFromMatch(match: RegExpExecArray): DateParts {
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

function isValidDateParts({
  year,
  month,
  day,
  hour,
  minute,
  second,
}: DateParts): boolean {
  if (![year, month, day, hour, minute, second].every(Number.isInteger))
    return false;
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return false;
  }
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function timeZoneOffsetMinutes(
  date: Date,
  timeZone: string,
): number | undefined {
  try {
    const parts = datePartsInTimeZone(date, timeZone);
    const representedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );
    return Math.round((representedAsUtc - date.getTime()) / 60_000);
  } catch {
    return undefined;
  }
}

function localPartsMatch(
  date: Date,
  timeZone: string,
  expected: DateParts,
): boolean {
  try {
    const actual = datePartsInTimeZone(date, timeZone);
    return Object.keys(expected).every(
      (key) =>
        actual[key as keyof typeof actual] ===
        expected[key as keyof typeof expected],
    );
  } catch {
    return false;
  }
}

function datePartsInTimeZone(date: Date, timeZone: string): DateParts {
  let formatter = datePartsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    datePartsFormatters.set(timeZone, formatter);
  }
  const values = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  ) as Record<string, number>;
  const { year, month, day, hour, minute, second } = values;
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    second === undefined
  ) {
    throw new RangeError(
      `Could not resolve calendar parts for time zone: ${timeZone}`,
    );
  }
  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
  };
}

function formatOffset(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absolute = Math.abs(offsetMinutes);
  return `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, "0");
}
